import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Download, PlusSquare, Share2, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { HOME_SCREEN_APP_NAME } from '../constants/branding';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type Props = {
  alwaysVisible?: boolean;
  mode?: 'floating' | 'embedded';
};

const DISMISS_KEY = 'customer_app_install_prompt_dismissed_v1';

const isIosDevice = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent);
const isSafariOnIos = () => isIosDevice() && /Safari/i.test(window.navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

const isProbablyMobile = () => {
  const ua = window.navigator.userAgent;
  const mobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const narrowViewport = window.innerWidth <= 820;
  return mobileUA || narrowViewport;
};

const InstallPrompt: React.FC<Props> = ({ alwaysVisible = false, mode = 'floating' }) => {
  const location = useLocation();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const ios = isIosDevice();

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      setInstallEvent(null);
      setDismissed(true);
      setShowIosHelp(false);
      localStorage.setItem(DISMISS_KEY, '1');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const hidden = useMemo(() => {
    if (alwaysVisible) return false;
    if (dismissed && !isIosDevice()) return true;
    if (!isProbablyMobile()) return true;
    if (isStandalone()) return true;
    return false;
  }, [alwaysVisible, dismissed]);

  const bottomOffset = location.pathname.startsWith('/reserve') || location.pathname === '/messages'
    ? 'bottom-20'
    : 'bottom-4';

  const closePrompt = () => {
    setDismissed(true);
    setShowIosHelp(false);
    if (!isIosDevice()) {
      localStorage.setItem(DISMISS_KEY, '1');
    }
  };

  const handleInstall = async () => {
    if (installEvent) {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      if (choice.outcome === 'accepted') {
        setDismissed(true);
        if (!isIosDevice()) {
          localStorage.setItem(DISMISS_KEY, '1');
        }
      }
      return;
    }

    if (isIosDevice()) {
      setShowIosHelp(true);
    }
  };

  if (hidden) return null;

  const containerClassName =
    mode === 'embedded'
      ? 'mt-5 w-full'
      : `fixed left-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 ${bottomOffset}`;

  return (
    <div className={containerClassName}>
      <div className="overflow-hidden rounded-3xl border border-emerald-100 bg-white/95 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3 px-4 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Put {HOME_SCREEN_APP_NAME} on your home screen</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Create an icon on your phone so you can open {HOME_SCREEN_APP_NAME} with one tap.
                </p>
              </div>

              {!alwaysVisible && (
                <button
                  type="button"
                  onClick={closePrompt}
                  className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Close install prompt"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mt-3 flex flex-col gap-2">
              {!ios && (
                <button
                  type="button"
                  onClick={() => {
                    void handleInstall();
                  }}
                  className="flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  <Download className="h-4 w-4" />
                  Install on Android
                </button>
              )}

              {ios ? (
                <button
                  type="button"
                  onClick={() => setShowIosHelp((prev) => !prev)}
                  className="flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  <Share2 className="h-4 w-4" />
                  Show steps for iPhone
                </button>
              ) : (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-center text-xs leading-5 text-emerald-700">
                  iPhone uses manual steps. Android can open the install prompt directly.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showIosHelp && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/45 p-3 sm:items-center">
          <div className="w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-100">iPhone Setup</p>
                  <h3 className="mt-1 text-lg font-semibold">Add the {HOME_SCREEN_APP_NAME} icon to your home screen</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowIosHelp(false)}
                  className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                  aria-label="Close iPhone install guide"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 px-4 py-4">
              {!isSafariOnIos() && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                  If this does not work on this screen, open the same page in Safari and try the steps there.
                </div>
              )}

              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-4">
                <div className="text-sm font-semibold text-slate-900">Tap these buttons in order</div>
                <div className="mt-3 flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm">
                  <div className="text-sm font-medium text-slate-700">Bottom menu</div>
                  <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">
                    <Share2 className="h-4 w-4" />
                    <span className="text-sm font-semibold">Share</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                    1
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Tap the Share button</div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <Share2 className="h-4 w-4 text-emerald-600" />
                      Look near the bottom of the screen and tap this icon.
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                    2
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Tap “Add to Home Screen”</div>
                    <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                      <PlusSquare className="h-4 w-4 text-emerald-600" />
                      Add to Home Screen
                      <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                    3
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Tap Add</div>
                    <div className="mt-2 flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-2">
                      <span className="text-sm text-slate-600">App name</span>
                      <span className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">
                        Add
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      When this is done, a {HOME_SCREEN_APP_NAME} icon will appear on your phone home screen.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowIosHelp(false)}
                className="flex min-h-[48px] w-full items-center justify-center rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
